import EmptyState from '@rio-cloud/rio-uikit/EmptyState';
import ErrorState from '@rio-cloud/rio-uikit/ErrorState';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { orderBy } from 'es-toolkit/compat';
import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { getHasError, getIsLoading } from '../../api/redux/api.redux';
import { ApiCalls } from '../../api/redux/types';
import { InvoicesMonth } from './InvoicesMonth';
import { fetchInvoicesThunk } from './invoices.thunk';
import { getInvoices } from './redux/invoices.redux';
import type { DatedInvoices } from './redux/types';

function wrapInDiv(content: React.ReactNode) {
    return <div className='InvoicesTable container-fluid fluid-default'>{content}</div>;
}

export function Invoices() {
    const dispatch = useDispatch<RootDispatch>();
    const invoices = useSelector((state: RootState) => getInvoices(state));
    const hasError = useSelector((state: RootState) => getHasError(state, ApiCalls.INVOICES_GET));
    const isLoading = useSelector((state: RootState) => getIsLoading(state, ApiCalls.INVOICES_GET));

    useEffect(() => {
        dispatch(fetchInvoicesThunk);
    }, [dispatch]);

    const processedInvoices = useMemo(() => {
        const orderedByMonth = orderBy<DatedInvoices>(invoices, ['invoiceDate'], ['desc']);

        return orderedByMonth.map(invoiceMonth => ({
            ...invoiceMonth,
            invoices: invoiceMonth.invoices.filter(invoice => invoice.amount !== 0),
        }));
    }, [invoices]);

    const hasNonZeroInvoices = useMemo(
        () => processedInvoices.some(invoiceMonth => invoiceMonth.invoices.length > 0),
        [processedInvoices]
    );

    if (isLoading) {
        return wrapInDiv(<Spinner text={<FormattedMessage id={'intl-msg:marketplace.loading'} />} isInverse={false} />);
    }

    if (hasError) {
        return wrapInDiv(
            <ErrorState headline={<FormattedMessage id={'intl-msg:marketplace.invoices.fetch.error'} />} />
        );
    }

    if (!hasNonZeroInvoices) {
        return wrapInDiv(
            <EmptyState
                headline={<FormattedMessage id={'intl-msg:marketplace.invoices.not.found'} />}
                message={<FormattedMessage id={'intl-msg:marketplace.invoices.not.found.message'} />}
                outerClassName='margin-top-15pct'
            />
        );
    }

    return wrapInDiv(
        <div className='InvoiceList'>
            {processedInvoices
                .filter(invoiceMonth => invoiceMonth.invoices.length > 0)
                .map((invoiceMonth, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: index is stable for filtered invoice months
                    <InvoicesMonth invoicesMonth={invoiceMonth} key={index} />
                ))}
        </div>
    );
}

export const InvoicesContainer = Invoices;
